import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Briefcase, Eye, User, Mail, Phone, Calendar, MapPin, Download, FileSpreadsheet, ChevronLeft, ChevronRight, GraduationCap, Building2, Filter, RefreshCw, Search, Users, CheckCircle, XCircle, Clock, UserCheck, Award, TrendingUp } from 'lucide-react';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';

// Application statuses with colors
const APPLICATION_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-700', icon: Users },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-amber-100 text-amber-700', icon: Calendar },
  { value: 'on_hold', label: 'On Hold', color: 'bg-slate-100 text-slate-700', icon: Clock },
  { value: 'selected', label: 'Selected', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'offer_made', label: 'Offer Made', color: 'bg-teal-100 text-teal-700', icon: Award },
  { value: 'joined', label: 'Joined', color: 'bg-emerald-100 text-emerald-700', icon: UserCheck },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle }
];

const ApplicationsMonitor = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [viewingHistory, setViewingHistory] = useState(null);
  
  // Filter states - Default to last 90 days
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultToDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const [statusFilter, setStatusFilter] = useState('all');
  const [employerFilter, setEmployerFilter] = useState('all');
  const [jobIdFilter, setJobIdFilter] = useState('');
  
  // Filter options
  const [employers, setEmployers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState('');
  const [showJobSuggestions, setShowJobSuggestions] = useState(false);
  const [selectedJobTitle, setSelectedJobTitle] = useState('');
  
  // Status counts
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    new: 0,
    shortlisted: 0,
    interview_scheduled: 0,
    on_hold: 0,
    selected: 0,
    offer_made: 0,
    joined: 0,
    rejected: 0
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch applications when filters change
  useEffect(() => {
    fetchApplications();
  }, [fromDate, toDate, statusFilter, employerFilter, jobIdFilter, currentPage]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/admin/applications/filters');
      setEmployers(response.data.employers || []);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  // Fetch status counts
  const fetchStatusCounts = async () => {
    try {
      const params = [];
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      if (employerFilter !== 'all') params.push(`employer_id=${employerFilter}`);
      if (jobIdFilter.trim()) params.push(`job_id=${jobIdFilter.trim()}`);
      
      const url = '/admin/applications/status-counts?' + params.join('&');
      const response = await api.get(url);
      setStatusCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  };

  // Fetch status counts when filters change
  useEffect(() => {
    fetchStatusCounts();
  }, [fromDate, toDate, employerFilter, jobIdFilter]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = [`page=${currentPage}`, `limit=${limit}`];
      
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (employerFilter !== 'all') params.push(`employer_id=${employerFilter}`);
      if (jobIdFilter.trim()) params.push(`job_id=${jobIdFilter.trim()}`);
      
      const url = '/admin/applications?' + params.join('&');
      const response = await api.get(url);
      
      setApplications(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch applications');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, fromDate, toDate, statusFilter, employerFilter, jobIdFilter]);

  const viewDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailDialog(true);
  };

  const handleExport = async (format) => {
    try {
      let url = `/admin/applications/export?format=${format}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (employerFilter !== 'all') url += `&employer_id=${employerFilter}`;
      if (jobIdFilter.trim()) url += `&job_id=${jobIdFilter.trim()}`;
      
      window.open(getBackendUrl() + '/api' + url, '_blank');
      toast.success(`Exporting to ${format.toUpperCase()}...`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const clearFilters = () => {
    setFromDate(getDefaultFromDate());
    setToDate(getDefaultToDate());
    setStatusFilter('all');
    setEmployerFilter('all');
    setJobIdFilter('');
    setSelectedJobTitle('');
    setJobSearch('');
    setCurrentPage(1);
  };

  const handleJobSelect = (job) => {
    setJobIdFilter(job.id);
    setSelectedJobTitle(job.job_title);
    setJobSearch('');
    setShowJobSuggestions(false);
    setCurrentPage(1);
  };

  const clearJobFilter = () => {
    setJobIdFilter('');
    setSelectedJobTitle('');
    setJobSearch('');
  };

  const filteredJobs = jobSearch.trim()
    ? jobs.filter(j => 
        j.job_title?.toLowerCase().includes(jobSearch.toLowerCase()) ||
        j.company_name?.toLowerCase().includes(jobSearch.toLowerCase()) ||
        j.id?.toLowerCase().includes(jobSearch.toLowerCase())
      ).slice(0, 10)
    : [];

  const setLast30Days = () => {
    setFromDate(getDefaultFromDate());
    setToDate(getDefaultToDate());
    setCurrentPage(1);
  };

  const setLast7Days = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    setFromDate(date.toISOString().split('T')[0]);
    setToDate(getDefaultToDate());
    setCurrentPage(1);
  };

  const setToday = () => {
    const today = getDefaultToDate();
    setFromDate(today);
    setToDate(today);
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  const downloadCV = (cvPath) => {
    if (!cvPath) {
      toast.error('No CV uploaded');
      return;
    }
    const url = getImageUrl(cvPath);
    window.open(url, '_blank');
  };

  const getStatusBadge = (status) => {
    const statusConfig = APPLICATION_STATUSES.find(s => s.value === status) || APPLICATION_STATUSES[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  // Get applicant name from profile or user
  const getApplicantName = (app) => {
    return app.profile?.full_name || app.user?.name || app.job_seeker?.full_name || 'N/A';
  };

  // Get mobile from user (primary) or profile (fallback)
  const getApplicantMobile = (app) => {
    return app.user?.mobile || app.profile?.mobile || app.job_seeker?.mobile || 'N/A';
  };

  // Get email from profile or user
  const getApplicantEmail = (app) => {
    return app.profile?.email || app.user?.email || app.job_seeker?.email || 'N/A';
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Applications Monitor</h1>
          <p className="text-slate-500 text-sm mt-1">
            Showing last 3 months by default • Track all application status updates by employers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchApplications(); fetchStatusCounts(); }}
            className="text-slate-600"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="text-slate-600"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            className="text-slate-600"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-3 mb-6">
        <Card 
          className={`p-3 border-slate-200 cursor-pointer hover:border-teal-400 transition-colors ${statusFilter === 'all' ? 'ring-2 ring-teal-500' : ''}`}
          onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
          data-testid="status-card-total"
        >
          <div className="flex items-center justify-between mb-1">
            <TrendingUp className="w-4 h-4 text-slate-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{statusCounts.total || 0}</p>
          <p className="text-xs text-slate-500">Total</p>
        </Card>
        {APPLICATION_STATUSES.map(status => {
          const IconComponent = status.icon;
          const count = statusCounts[status.value] || 0;
          const isActive = statusFilter === status.value;
          return (
            <Card 
              key={status.value}
              className={`p-3 cursor-pointer hover:border-teal-400 transition-colors ${isActive ? 'ring-2 ring-teal-500' : 'border-slate-200'}`}
              onClick={() => { setStatusFilter(status.value); setCurrentPage(1); }}
              data-testid={`status-card-${status.value}`}
            >
              <div className="flex items-center justify-between mb-1">
                <IconComponent className={`w-4 h-4 ${status.color.replace('bg-', 'text-').replace('-100', '-600')}`} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-xs text-slate-500 truncate" title={status.label}>{status.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Quick Date Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={setToday} data-testid="filter-today">
          Today
        </Button>
        <Button variant="outline" size="sm" onClick={setLast7Days} data-testid="filter-7days">
          Last 7 Days
        </Button>
        <Button variant="outline" size="sm" onClick={setLast30Days} data-testid="filter-30days">
          Last 30 Days
        </Button>
        <Button variant="outline" size="sm" onClick={() => {
          const date = new Date();
          date.setDate(date.getDate() - 90);
          setFromDate(date.toISOString().split('T')[0]);
          setToDate(new Date().toISOString().split('T')[0]);
          setCurrentPage(1);
        }} data-testid="filter-90days">
          Last 90 Days
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">From Date</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
              data-testid="from-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To Date</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
              data-testid="to-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Employer</label>
            <Select value={employerFilter} onValueChange={(val) => { setEmployerFilter(val); setCurrentPage(1); }}>
              <SelectTrigger data-testid="employer-filter-select">
                <SelectValue placeholder="All Employers" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all">All Employers</SelectItem>
                {employers.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} {emp.type === 'prime' && '(Prime)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="block text-xs text-slate-500 mb-1">Job Posting</label>
            {selectedJobTitle ? (
              <div className="flex items-center gap-2 h-10 px-3 border border-slate-300 rounded-md bg-slate-50">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span className="text-sm flex-1 truncate" title={selectedJobTitle}>{selectedJobTitle}</span>
                <button 
                  onClick={clearJobFilter}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search job posting..."
                  value={jobSearch}
                  onChange={(e) => { setJobSearch(e.target.value); setShowJobSuggestions(true); }}
                  onFocus={() => jobSearch && setShowJobSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowJobSuggestions(false), 200)}
                  className="pl-8"
                  data-testid="job-filter-input"
                />
              </div>
            )}
            {showJobSuggestions && filteredJobs.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="px-3 py-2 cursor-pointer hover:bg-slate-100"
                    onMouseDown={() => handleJobSelect(job)}
                  >
                    <p className="text-sm font-medium text-slate-900 truncate">{job.job_title}</p>
                    <p className="text-xs text-slate-500">{job.company_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Status</label>
            <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {APPLICATION_STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
              data-testid="clear-filters-btn"
            >
              Clear All
            </Button>
          </div>
        </div>
      </Card>

      {/* Applications Grid Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Application Date</TableHead>
                <TableHead className="font-semibold">Job Seeker Name</TableHead>
                <TableHead className="font-semibold">Mobile</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Job Title</TableHead>
                <TableHead className="font-semibold">Company</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Experience Req.</TableHead>
                <TableHead className="font-semibold">Education</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">CV</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading applications...</p>
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No applications found</p>
                    <p className="text-slate-400 text-sm mt-1">Try adjusting your filters or date range</p>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app, index) => (
                  <TableRow key={app.id} className="hover:bg-slate-50">
                    <TableCell className="text-center font-medium">{((currentPage - 1) * limit) + index + 1}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(app.applied_at)}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">
                        {getApplicantName(app)}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 font-medium">
                      {getApplicantMobile(app)}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[150px] truncate" title={getApplicantEmail(app)}>
                      {getApplicantEmail(app)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900 max-w-[150px] truncate" title={app.job_title || app.job?.job_title}>
                        {app.job_title || app.job?.job_title || 'N/A'}
                      </div>
                      <div className="text-xs text-slate-400">{app.job_id}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[120px] truncate">
                      {app.company_name || app.job?.company_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {app.job?.locations?.slice(0, 1).map(loc => loc.city).join(', ') || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                      {app.job ? `${app.job.min_experience}-${app.job.max_experience} yrs` : 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[100px] truncate">
                      {app.profile?.education?.[0]?.degree || app.profile?.education?.[0]?.qualification || 'N/A'}
                    </TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell className="text-center">
                      {(app.profile?.cv || app.job_seeker?.cv) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadCV(app.profile?.cv || app.job_seeker?.cv)}
                          className="text-teal-600 hover:text-teal-700"
                          data-testid={`download-cv-${app.id}`}
                          title="Download CV"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-slate-400 text-xs">No CV</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewDetails(app)}
                        data-testid={`view-application-${app.id}`}
                        className="text-slate-600 hover:text-teal-600"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              {total > 0 ? (
                <>Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} applications</>
              ) : (
                <>No applications found</>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-6">
              {/* Applicant Information */}
              <div className="flex items-center gap-4">
                {(selectedApplication.profile?.profile_picture || selectedApplication.job_seeker?.profile_picture) ? (
                  <img 
                    src={getImageUrl(selectedApplication.profile?.profile_picture || selectedApplication.job_seeker?.profile_picture)} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-4 border-slate-200" 
                  />
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {getApplicantName(selectedApplication)}
                  </h3>
                  <p className="text-slate-600">{getApplicantEmail(selectedApplication)}</p>
                  <div className="mt-2 flex items-center gap-3">
                    {getStatusBadge(selectedApplication.status)}
                    <span className="text-sm text-slate-500">
                      Applied: {formatDateTime(selectedApplication.applied_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{getApplicantMobile(selectedApplication)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{getApplicantEmail(selectedApplication)}</span>
                    </div>
                    {(selectedApplication.profile?.city || selectedApplication.job_seeker?.city) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>
                          {[
                            selectedApplication.profile?.city || selectedApplication.job_seeker?.city, 
                            selectedApplication.profile?.district || selectedApplication.job_seeker?.district, 
                            selectedApplication.profile?.state || selectedApplication.job_seeker?.state
                          ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Job Applied For</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Job ID:</strong> {selectedApplication.job_id}</p>
                    <p><strong>Title:</strong> {selectedApplication.job_title || selectedApplication.job?.job_title}</p>
                    <p><strong>Company:</strong> {selectedApplication.company_name || selectedApplication.job?.company_name}</p>
                    {selectedApplication.job && (
                      <>
                        <p><strong>Experience:</strong> {selectedApplication.job.min_experience}-{selectedApplication.job.max_experience} years</p>
                        <p><strong>Salary:</strong> ₹{selectedApplication.job.min_salary?.toLocaleString()}-₹{selectedApplication.job.max_salary?.toLocaleString()}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Education Details */}
              {(selectedApplication.profile?.education || selectedApplication.job_seeker?.education) && 
               (selectedApplication.profile?.education?.length > 0 || selectedApplication.job_seeker?.education?.length > 0) && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-teal-600" />
                    Educational Information
                  </h4>
                  <div className="space-y-3">
                    {(selectedApplication.profile?.education || selectedApplication.job_seeker?.education || []).map((edu, idx) => (
                      <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{edu.degree || edu.qualification || 'N/A'}</p>
                            <p className="text-sm text-slate-600">{edu.institution || edu.university || 'N/A'}</p>
                          </div>
                          <span className="text-sm text-blue-700">{edu.year_of_passing || edu.to_year || 'N/A'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Details */}
              {(selectedApplication.profile?.experience || selectedApplication.job_seeker?.experience) && 
               (selectedApplication.profile?.experience?.length > 0 || selectedApplication.job_seeker?.experience?.length > 0) && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-teal-600" />
                    Experience Details
                  </h4>
                  <div className="space-y-3">
                    {(selectedApplication.profile?.experience || selectedApplication.job_seeker?.experience || []).map((exp, idx) => (
                      <div key={idx} className="p-4 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{exp.designation || exp.job_title || 'N/A'}</p>
                            <p className="text-sm text-slate-600">{exp.company || exp.company_name || 'N/A'}</p>
                          </div>
                          <span className="text-sm text-green-700">{exp.years || 0} years</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedApplication.profile?.cv || selectedApplication.job_seeker?.cv) && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => downloadCV(selectedApplication.profile?.cv || selectedApplication.job_seeker?.cv)}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CV
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationsMonitor;
